import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Icon } from "@/components/Icon";
import { useToast } from "@/context/ToastContext";
import { tenderApi, studyApi } from "@/lib/api";
import { Tender } from "@/pages/Tenders/types";

interface TenderStudyModalProps {
    isOpen: boolean;
    tender: Tender | null;
    isApproveMode?: boolean;
    isStudyMode?: boolean;
    onClose: () => void;
    onSuccess: (savedTender: Tender) => void;
}

export const TenderStudyModal: React.FC<TenderStudyModalProps> = ({
    isOpen,
    tender,
    isApproveMode = true,
    isStudyMode = false,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [bc3File, setBc3File] = useState<File | null>(null);
    const [percentage, setPercentage] = useState<string>("0");
    const [isBc3Dragging, setIsBc3Dragging] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [removedExistingFile, setRemovedExistingFile] =
        useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (isOpen && tender) {
            setBc3File(null);
            setRemovedExistingFile(false);
            setPercentage(
                tender.bc3_percentage !== undefined &&
                    tender.bc3_percentage !== null
                    ? String(tender.bc3_percentage)
                    : "0"
            );
        }
    }, [isOpen, tender]);

    if (!tender) {
        return null;
    }

    const clientName = tender.client?.name || "";
    const workTitle = tender.title || "";
    const provinceName =
        typeof tender.province === "object" && tender.province !== null
            ? tender.province.name
            : tender.province || "";
    const cityName = tender.city || "";
    const formattedBudget = new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
    }).format(Number(tender.budget || 0));

    const handleDragOver = (e: React.DragEvent<HTMLElement>): void => {
        e.preventDefault();
        setIsBc3Dragging(true);
    };

    const handleDragLeave = (): void => {
        setIsBc3Dragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLElement>): void => {
        e.preventDefault();
        setIsBc3Dragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (file.name.toLowerCase().endsWith(".bc3")) {
                setBc3File(file);
                setRemovedExistingFile(false);
            } else {
                showError(t("tender.error_invalid_bc3_format"));
            }
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files && e.target.files[0]) {
            setBc3File(e.target.files[0]);
            setRemovedExistingFile(false);
            e.target.value = "";
        }
    };

    const handleClearFile = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        e.stopPropagation();
        setBc3File(null);
        setRemovedExistingFile(true);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async (): Promise<void> => {
        if (!bc3File && !tender.bc3_file_path) {
            showError(t("tender.error_bc3_required"));
            return;
        }

        const numericPercentage = parseFloat(percentage) || 0;
        if (numericPercentage < 0 || numericPercentage > 100) {
            showError(t("tender.field_bc3_percentage"));
            return;
        }

        setIsSubmitting(true);
        try {
            let updated = tender;
            if (bc3File) {
                const res = isStudyMode
                    ? await studyApi.uploadBc3(
                          tender.id,
                          bc3File,
                          numericPercentage
                      )
                    : await tenderApi.uploadBc3(
                          tender.id,
                          bc3File,
                          numericPercentage,
                          isApproveMode
                      );
                if (res?.data) {
                    updated = res.data;
                }
            } else {
                if (isApproveMode) {
                    const res = await tenderApi.changeStatus(
                        tender.id,
                        5,
                        t("tender.passed_to_studies_success")
                    );
                    if (res?.data) {
                        updated = res.data;
                    }
                }
            }

            const successMsg = isApproveMode
                ? t("tender.passed_to_studies_success")
                : t("tender.saved_success");

            showSuccess(successMsg);
            onSuccess(updated);
            onClose();
        } catch (error: any) {
            console.error(error);
            const errorMsg =
                error?.response?.data?.message ||
                error?.message ||
                t("common.error");
            showError(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentFileName = bc3File
        ? bc3File.name
        : !removedExistingFile && tender.bc3_file_name
          ? tender.bc3_file_name
          : null;

    const title = isApproveMode
        ? t("tender.modal_approve_study_title")
        : t("tender.modal_edit_study_title");

    const submitLabel = isApproveMode
        ? t("tender.btn_send_to_study")
        : t("common.save");

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            maxWidth="lg"
            onSubmit={handleSubmit}
            submitLabel={submitLabel}
            cancelLabel={t("common.close")}
            isSubmitting={isSubmitting}
        >
            <div className="space-y-3">
                <Input
                    label={t("tender.field_client")}
                    value={clientName}
                    disabled={true}
                />
                <Input
                    label={t("tender.field_title")}
                    value={workTitle}
                    disabled={true}
                />
                <Input
                    label={t("tender.field_province")}
                    value={provinceName}
                    disabled={true}
                />
                <Input
                    label={t("tender.field_city")}
                    value={cityName}
                    disabled={true}
                />
                <Input
                    label={t("tender.col_budget")}
                    value={formattedBudget}
                    disabled={true}
                />
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-zinc-300 block mb-1.5">
                            {t("tender.field_bc3_required")}
                        </label>
                        <label
                            className={`relative block rounded-xl border-2 border-dashed p-6 text-center transition-colors cursor-pointer hover:border-primary/60 hover:bg-slate-50 dark:hover:bg-white/5 ${
                                isBc3Dragging
                                    ? "border-primary bg-primary/5 dark:bg-primary/10"
                                    : "border-slate-300 dark:border-white/10"
                            }`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            <Icon
                                value="fileText"
                                size={32}
                                className={`mx-auto mb-2 ${isBc3Dragging ? "text-primary" : "text-slate-400 dark:text-zinc-500"}`}
                            />
                            <p className="text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                {currentFileName || t("tender.field_bc3_file")}
                            </p>
                            {currentFileName ? (
                                <button
                                    type="button"
                                    onClick={handleClearFile}
                                    className="mt-1 inline-flex items-center text-xs text-rose-500 hover:text-rose-700 transition-colors cursor-pointer font-medium"
                                >
                                    {t("common.cancel")}
                                </button>
                            ) : (
                                <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                                    {t("tender.drag_drop_bc3")}
                                </p>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".bc3"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </label>
                    </div>

                    <Input
                        label={t("tender.field_bc3_percentage_default")}
                        type="number"
                        value={percentage}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setPercentage(e.target.value)
                        }
                        min={0}
                        max={100}
                    />
                </div>
            </div>
        </Modal>
    );
};
